import { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  ICreatePositionRequest,
  IEditPositionRequest,
  IPositionResponse,
  IPositionResponseChangeLevel,
  IPositionResponseNew,
  IRequestDataAddKnowledgeInPosition,
  IRequestDataChangeOrderKnowledgeArea,
} from './position.interface';

import { axiosApiInstance } from 'services/config';
import { IPosition } from 'types/domain/position';
import {
  convertAllPositionByExpertise,
  convertPosition,
  convertPositionToDetailsView,
  convertPositionWithSortedLevels,
} from 'utils';

class PositionAPI {
  async getAllPositionsByExpertise(
    expertizeId: number,
    config?: AxiosRequestConfig,
  ): Promise<IPositionResponseNew[]> {
    const response: AxiosResponse<IPositionResponseNew[]> = await axiosApiInstance.get(
      `position/expertise/${expertizeId}`,
      config,
    );

    return convertAllPositionByExpertise(response.data);
  }

  async getPosition(positionId: number): Promise<IPositionResponseNew> {
    const response: AxiosResponse<IPositionResponseNew> = await axiosApiInstance.get(
      `position/${positionId}`,
    );
    return convertPosition(response.data);
  }

  async createPosition(
    createPositionData: ICreatePositionRequest,
    config?: AxiosRequestConfig,
  ): Promise<IPositionResponseNew> {
    const response: AxiosResponse<IPositionResponseNew> = await axiosApiInstance.post(
      'position',
      {
        name: createPositionData.name,
        orderNo: createPositionData.orderNo,
        expertizeId: createPositionData.expertiseId,
      },
      config,
    );
    return response.data;
  }

  async editPosition(
    positionId: number,
    positionData: IEditPositionRequest,
    config?: AxiosRequestConfig,
  ): Promise<IPosition> {
    const response: AxiosResponse<IPositionResponse> = await axiosApiInstance.put(
      `position/${positionId}`,
      positionData,
      config,
    );

    return convertPositionToDetailsView(response.data);
  }

  async deletePosition(positionId: number, config?: AxiosRequestConfig): Promise<void> {
    await axiosApiInstance.delete(`position/${positionId}`, config);
  }

  async addKnowledgeAreasInPosition(
    positionId: number,
    requestData: IRequestDataAddKnowledgeInPosition[],
    config?: AxiosRequestConfig,
  ): Promise<IPositionResponseNew> {
    const data = {
      knowledgeAreaIds: requestData,
    };

    const response: AxiosResponse<IPositionResponseNew> = await axiosApiInstance.put(
      `position/${positionId}/knowledge-area/add`,
      data,
      config,
    );

    return convertPosition(response.data);
  }

  async deleteSelectedLevelsFromKnowledgeAreaPosition(
    dataRequest: {
      knowledgeAreaPositionId: number;
    }[],
    config?: AxiosRequestConfig,
  ): Promise<void> {
    await axiosApiInstance.delete(`position/knowledge-area-position/selected-levels`, {
      ...config,
      data: dataRequest,
    });
  }

  async changePositionKnowledgeAreaLevel(
    positionId: number,
    body: { id: number; levelIds: number[] },
    config?: AxiosRequestConfig,
  ): Promise<IPositionResponseChangeLevel> {
    const response: AxiosResponse<IPositionResponseChangeLevel> =
      await axiosApiInstance.put(
        `position/${positionId}/knowledge-area/level`,
        body,
        config,
      );
    return convertPositionWithSortedLevels(response.data);
  }

  async changeOrderKnowledgeAreasInPosition(
    requestData: IRequestDataChangeOrderKnowledgeArea[],
    config?: AxiosRequestConfig,
  ): Promise<IPositionResponseNew['knowledgeAreas']> {
    const response = await axiosApiInstance.put(
      'position/knowledge-area/order',
      requestData,
      config,
    );

    return response.data;
  }

  async unsubscribePosition(positionId: number): Promise<IPositionResponseNew> {
    const response: AxiosResponse<IPositionResponseNew> = await axiosApiInstance.put(
      `position/${positionId}/unsubscribe`,
    );

    return convertPosition(response.data);
  }

  async deleteKnowledgeAreasFromPosition(
    dataRequest: { knowledgeAreaPositionId: number }[],
    config?: AxiosRequestConfig,
  ): Promise<void> {
    await axiosApiInstance.delete(`position/knowledge-area-position/batch-delete`, {
      ...config,
      data: dataRequest,
    });
  }
}

export { PositionAPI };
